import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../helpers/request";
import qs from "querystringify";
import { ILiveStream, IUser } from "../../types/models";

interface AdminState {
  activeLiveStreamsLoading: boolean;
  activeLiveStreams: ILiveStream[];
  usersLoading: boolean;
  users: IUser[];
  usersCount: number;
  usersPage: number;
  usersPerPage: number;
  usersTotalPages: number;
  error: null | any;
  userById: null | any;
}

const initialState: AdminState = {
  activeLiveStreamsLoading: false,
  activeLiveStreams: [],
  usersLoading: false,
  users: [],
  usersCount: 0,
  usersPage: 1,
  usersPerPage: 25,
  usersTotalPages: 0,
  error: null,
  userById: null,
};

export const getAdminActiveLiveStreams = createAsyncThunk(
  "getAdminActiveLiveStreams",
  async () => {
    const res = await axiosInstance.get("/admin/live-stream/active");
    return await res.data;
  }
);

export const updateLiveStreamPromoted = createAsyncThunk(
  "updateLiveStreamPromoted",
  async ({ id, promotedAccepted }: { id: string; promotedAccepted: boolean }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `/admin/live-stream/${id}/promotedAccepted`,
        { promotedAccepted }
      );
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const adminRestartLiveStream = createAsyncThunk(
  "adminRestartLiveStream",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/admin/live-stream/${id}/restart`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const adminRestartAllLiveStreams = createAsyncThunk(
  "adminRestartAllLiveStreams",
  async (_id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/live-stream/restart-all`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const adminEndLiveStream = createAsyncThunk(
  "adminEndLiveStream",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/admin/live-stream/${id}/end`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getAdminUsers = createAsyncThunk(
  "getAdminUsers",
  async (query: any = {}) => {
    const res = await axiosInstance.get(`/admin/users?${qs.stringify(query)}`);
    return await res.data;
  }
);

export const adminChangeUserStatus = createAsyncThunk(
  "adminChangeUserStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/users/status`, {
        ...data,
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const adminDeleteUser = createAsyncThunk(
  "adminDeleteUser",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/admin/users/${id}`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const adminImpersonateUser = createAsyncThunk(
  "adminImpersonateUser",
  async (userId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/users/impersonate`, {
        userId,
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const AdminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // active live stream
    builder.addCase(getAdminActiveLiveStreams.pending, (state: any) => {
      state.activeLiveStreamsLoading = true;
    });
    builder.addCase(
      getAdminActiveLiveStreams.fulfilled,
      (state: any, { payload }: { payload: any }) => {
        state.activeLiveStreamsLoading = false;
        state.activeLiveStreams = payload;
      }
    );
    builder.addCase(
      getAdminActiveLiveStreams.rejected,
      (state: any, { payload }: { payload: any }) => {
        state.activeLiveStreamsLoading = false;
        state.error = payload;
      }
    );

    // users
    builder.addCase(getAdminUsers.pending, (state: any) => {
      state.usersLoading = true;
    });
    builder.addCase(
      getAdminUsers.fulfilled,
      (state: any, { payload }: { payload: any }) => {
        state.usersLoading = false;
        state.users = payload.users;
        state.usersCount = payload.count;
        state.usersPage = payload.page;
        state.usersPerPage = payload.perPage;
        state.usersTotalPages = payload.totalPages;
      }
    );
    builder.addCase(
      getAdminUsers.rejected,
      (state: any, { payload }: { payload: any }) => {
        state.usersLoading = false;
        state.error = payload;
      }
    );
  },
});

export const AdminReducer = AdminSlice.reducer;
